import { RxStateComponent } from '@alliance/shared/models'
import { DOCUMENT } from '@angular/common'
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Inject, Input, Output } from '@angular/core'
import { PolymorpheusContent } from '@alliance/shared/polymorpheus'
import { fromEvent } from 'rxjs'
import { filter, takeUntil } from 'rxjs/operators'
import { DropDownDirectionEnum, DropDownVerticalDirectionEnum } from './drop-down-direction-enum'
import { DropDownType } from './drop-down-type'

@Component({
  selector: 'santa-drop-down',
  templateUrl: './drop-down.component.html',
  styleUrls: ['./drop-down.component.tw.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SantaDropDownComponent<T, K> extends RxStateComponent<{
  isOpened: boolean
}> {
  @Input() public type: DropDownType = 'normal'
  @Input() public buttonContent: PolymorpheusContent | undefined
  @Input() public dropDownContent: PolymorpheusContent | undefined
  @Input() public buttonContext: T | undefined
  @Input() public dropDownContext: K | undefined
  @Input() public showArrow = true
  @Input() public hideDivider = false
  @Input() public initOnStart = true
  @Input() public direction = DropDownDirectionEnum.left
  @Input() public verticalDirection = DropDownVerticalDirectionEnum.bottom
  @Input() public fullWidth = true
  @Input() public enabledShadow = true
  @Input() public closeOnClickInDropDown = true
  @Input() public closeOnClickOutside = true
  @Input() public openOnClick = true
  @Input() public contentFullWidth = false
  @Input() public dropDownZIndex = '30'
  @Input() public buttonSantaBgColor = 'santa-bg-transparent'
  @Input() public invalid = false
  @Input() public isContentHidden = false

  @Output() public openedChanges = new EventEmitter<boolean>()
  @Output() public toggleClick = new EventEmitter<void>()

  public readonly arrowIconLink = 'https://images.cf-rabota.com.ua/employer-account/icons/chevron-grey-bottom.svg'
  public readonly dropDownDirectionConfig: Record<DropDownDirectionEnum, string> = {
    [DropDownDirectionEnum.left]: 'content-left-direction',
    [DropDownDirectionEnum.right]: 'content-right-direction',
    [DropDownDirectionEnum.fullSize]: 'content-full-size'
  }
  public readonly verticalDirectionConfig: Record<DropDownVerticalDirectionEnum, string> = {
    [DropDownVerticalDirectionEnum.top]: 'vertical-top-direction',
    [DropDownVerticalDirectionEnum.bottom]: 'vertical-bottom-direction'
  }

  public readonly state = this.connectViewState(['isOpened'])

  public constructor(@Inject(DOCUMENT) private document: Document, private dropDownRef: ElementRef<HTMLElement>) {
    super()

    this.initState({
      isOpened: false
    })
  }

  @Input()
  public set opening(value: boolean) {
    this.setOpenedState(value)
  }

  public onOpenClick(event: Event): void {
    event.stopPropagation()
    event.preventDefault()
    if (this.openOnClick) {
      this.toggleClick.emit()
      this.setOpenedState(!this.get().isOpened)
    }
  }

  public onInDropDownClick(event: Event): void {
    event.stopPropagation()
    if (this.closeOnClickInDropDown) {
      this.setOpenedState(false)
    }
  }

  private setOpenedState(isOpened: boolean): void {
    if (isOpened !== this.get().isOpened) {
      this.set({ isOpened })
      this.openedChanges.emit(isOpened)

      if (this.closeOnClickOutside && isOpened) {
        this.hold(fromEvent(this.document, 'mousedown').pipe(takeUntil(this.select('isOpened').pipe(filter(value => !value)))), event => {
          const isClickedInside = (event.target instanceof Node && this.dropDownRef.nativeElement.contains(event.target)) || event.target === this.document.documentElement

          if (!isClickedInside) {
            this.setOpenedState(false)
          }
        })
      }
    }
  }
}
